import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, shareReplay, startWith, take, takeUntil } from 'rxjs/operators';

// Redux
import { dispatchInfoMessage, navigate, openPrintDialog, openWindow, setBrowserTitle, setTitle } from 'src/app/actions/core.actions';
import * as BookmarkProblemListActions from 'src/app/actions/bookmark-problem-list.actions';
import {
  copyBookmarkProblemList,
  findBookmarkProblemListProblemImages,
  initializeCopyBookmarkProblemListState,
  initializeFindBookmarkProblemListProblemImagesState,
  initializeReorderBookmarkProblemListProblemsState,
  reorderBookmarkProblemListProblems
} from 'src/app/actions/bookmark-problem-list.actions';
import * as BookmarkProblemsActions from 'src/app/actions/bookmark-problems.actions';
import { RootState } from 'src/app/reducers';
import {
  getBookmarkProblemListProblemsSearching,
  getCopyBookmarkProblemListResult,
  getReadableEnglishBookmarkProblemListProblems,
  getReadableHistoryBookmarkProblemListProblemsBySubject,
  getReadableNationalLanguageBookmarkProblemListProblems,
  getReadableScienceBookmarkProblemListProblemsBySubject,
  getReorderBookmarkProblemListProblemsResult
} from 'src/app/selectors/bookmark-problem-list.selectors';
import * as BookmarkProblemListSelectors from 'src/app/selectors/bookmark-problem-list.selectors';
import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import { getStaticCommonData } from 'src/app/selectors/static-data.selectors';
// models
import {
  GeneralProblems,
  ReadableEnglishProblem,
  ReadableHistoryProblem,
  ReadableNationalLanguageProblem,
  ReadableScienceProblem
} from 'src/app/models/problem';
import { Bookmark } from 'src/app/models/bookmark';
import { User } from 'src/app/models/user';
import { SearchBookmarkCondition } from '../../../models/search-bookmark-condition';
import { Visibility } from '../../../models/create-bookmark-problem-list';
import { ReorderBookmarkProblemListProblemsRequest } from '../../../models/reorder-bookmark-problem-list-problems';

// utils
import { Log } from 'src/app/utils/log';
import { Dates } from 'src/app/utils/dates';

// mappers
import { QueryParamsMapper } from '../../../mappers/query-params-mapper';

// services & components
import {
  ChangeBookmarkProblemListDialogComponent,
  ChangeBookmarkProblemListDialogData
} from '../change-bookmark-problem-list-dialog/change-bookmark-problem-list-dialog.component';
import {
  DeleteBookmarkProblemListDialogComponent,
  DeleteBookmarkProblemListDialogData
} from '../delete-bookmark-problem-list-dialog/delete-bookmark-problem-list-dialog.component';
import { BookmarkProblemListRouterService } from 'src/app/services/bookmark-problem-list-router.service';

// others
import {
  BOOKMARK_PROBLEM_LIST_DIALOG_WIDTH,
  BOOKMARK_RESULTS_PER_PAGE,
  BookmarkSortType,
  BookmarkSortTypeDisplayName,
  DIALOG_ZERO_PADDING_PANEL_CLASS,
  OTHER_ORGANIZATION_ID,
  PAGINATION_WINDOW_SIZE,
  SCIENCE_IDS,
  SEARCH_RESULTS_PER_PAGE,
  SUBJECT_NAMES,
  SubjectId
} from 'src/app/resources/config';
import { enter, fadeInOut } from 'src/app/resources/animations';
import { RoutingPathResolver } from '../../../app-routing-path-resolver';
import { FindBookmarkProblemsParams } from 'src/app/models/bookmark-problem';
import { PrintListenerService } from '../../../services/print-listener.service';
import { StaticCommonData } from 'src/app/models/static-common-data';

interface SelectableSortType {
  type: BookmarkSortType;
  displayName: string;
}

@Component({
  selector: 'app-bookmark-problem-list-detail',
  templateUrl: './bookmark-problem-list-detail.component.html',
  styleUrls: ['./bookmark-problem-list-detail.component.scss'],
  animations: [enter, fadeInOut]
})
export class BookmarkProblemListDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('pageTop') pageTop: ElementRef;
  staticCommonData$: Observable<StaticCommonData>;
  readableEnglishProblems$: Observable<ReadableEnglishProblem[]>;
  readableMathProblems$: Observable<ReadableScienceProblem[]>;
  readableNationalLanguageProblems$: Observable<ReadableNationalLanguageProblem[]>;
  readablePhysicsProblems$: Observable<ReadableScienceProblem[]>;
  readableChemistryProblems$: Observable<ReadableScienceProblem[]>;
  readableBiologyProblems$: Observable<ReadableScienceProblem[]>;
  readableJapaneseHistoryProblems$: Observable<ReadableHistoryProblem[]>;
  readableWorldHistoryProblems$: Observable<ReadableHistoryProblem[]>;
  readableGeographyProblems$: Observable<ReadableScienceProblem[]>;
  readablePoliticalEconomyProblems$: Observable<ReadableScienceProblem[]>;
  readableSpecialAdmissionsProblems$: Observable<ReadableScienceProblem[]>;

  bookmarkSearching$: Observable<boolean>;
  bookmarkProblemListProblems$: Observable<GeneralProblems>;

  ENGLISH_SUBJECT_ID: string;
  MATH_SUBJECT_ID: string;
  NATIONAL_LANGUAGE_SUBJECT_ID: string;
  PHYSICS_SUBJECT_ID: string;
  CHEMISTRY_SUBJECT_ID: string;
  BIOLOGY_SUBJECT_ID: string;
  JAPANESE_HISTORY_SUBJECT_ID: string;
  WORLD_HISTORY_SUBJECT_ID: string;
  GEOGRAPHY_SUBJECT_ID: string;
  POLITICAL_ECONOMY_SUBJECT_ID: string;
  SPECIAL_ADMISSIONS_SUBJECT_ID: string;

  user: User;
  totalBookmarkCount: number;
  selectableSortTypes: SelectableSortType[];
  selectedSortType: SelectableSortType;
  // 表示用
  bookmark: Bookmark;
  initializedBookmark$ = new BehaviorSubject(true);
  // pagination ----------
  currentPage = 1;
  totalPage: number;
  paginationPageSize: number;
  paginationWindowSize: number;
  private LOG_SOURCE = this.constructor.name;
  private title = 'お気に入りリスト問題一覧';
  private destroy$ = new Subject<void>();
  firstPageInitialized$ = of(false);

  subjectId: string;
  subjectName: string;
  signedInUser$: Observable<User>;
  bookmarkProblemListId: string; // クエリパラメタより取得したお気に入りリストID
  bookmarkProblemListName: string; // お気に入りリスト名
  lastUpdatedAt: string;
  isPrivate: boolean;
  ownerName: string;
  visibility: Visibility;

  protected readonly SubjectId = SubjectId;

  // 印刷用のプロパティ
  printMenuType: 'article' | 'spellcheck' | null = null;
  readableScienceProblemsForPrint$: Observable<ReadableScienceProblem[]>;
  isPrintReady = false; // 印刷準備が完了したかどうか
  private printPoller: ReturnType<typeof setInterval> | null = null;
  private printEndCallback: (() => void) | null = null;

  constructor(
    private store: Store<RootState>,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private dialog: MatDialog,
    private bookmarkProblemListRouterService: BookmarkProblemListRouterService,
    private printListenerService: PrintListenerService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.ENGLISH_SUBJECT_ID = SubjectId.ENGLISH;
    this.MATH_SUBJECT_ID = SubjectId.MATH;
    this.NATIONAL_LANGUAGE_SUBJECT_ID = SubjectId.NATIONAL_LANGUAGE;
    this.PHYSICS_SUBJECT_ID = SubjectId.PHYSICS;
    this.CHEMISTRY_SUBJECT_ID = SubjectId.CHEMISTRY;
    this.BIOLOGY_SUBJECT_ID = SubjectId.BIOLOGY;
    this.JAPANESE_HISTORY_SUBJECT_ID = SubjectId.JAPANESE_HISTORY;
    this.WORLD_HISTORY_SUBJECT_ID = SubjectId.WORLD_HISTORY;
    this.GEOGRAPHY_SUBJECT_ID = SubjectId.GEOGRAPHY;
    this.POLITICAL_ECONOMY_SUBJECT_ID = SubjectId.POLITICAL_ECONOMY;
    this.SPECIAL_ADMISSIONS_SUBJECT_ID = SubjectId.SPECIAL_ADMISSIONS;
    this.paginationPageSize = SEARCH_RESULTS_PER_PAGE;
    this.paginationWindowSize = PAGINATION_WINDOW_SIZE;
    this.setUpSelectableSortTypes();
  }

  ngOnInit() {
    this.store.dispatch(setBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));

    this.setUpStaticData();
    this.setUpUser();
    this.setUpBookmarkProblemListId();

    // ステートよりリスト情報を取得し、問題と問題画像取得APIを呼び出しReadableデータに変換する
    combineLatest([
      this.signedInUser$,
      this.store.select(BookmarkProblemListSelectors.getBookmarkProblemLists).pipe(filter(lists => lists != null && lists.length >= 0)),
      this.store
        .select(BookmarkProblemListSelectors.getSharedBookmarkProblemLists)
        .pipe(filter(lists => lists != null && lists.length >= 0))
    ])
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        map(([signedInUser, bookmarkLists, sharedLists]) => {
          const match = bookmarkLists.find(list => list.id === this.bookmarkProblemListId);
          if (match) {
            this.isPrivate = true;
            // 非公開のリストはリストのユーザーIDとログインしているユーザーIDのチェックを行う
            return match.userId === signedInUser.id ? match : null;
          }

          const sharedMatch = sharedLists.find(list => list.id === this.bookmarkProblemListId);
          if (sharedMatch) {
            this.isPrivate = false;
            // 共有されているリストは、団体種別「その他」以外で、同一団体であることをチェック
            return signedInUser.organization !== OTHER_ORGANIZATION_ID && signedInUser.schoolId === sharedMatch.schoolId
              ? sharedMatch
              : null;
          }

          return null;
        })
      )
      .subscribe(bookmarkList => {
        if (!bookmarkList) {
          this.totalBookmarkCount = 0;
          Log.warn(this.LOG_SOURCE, `お気に入りリストが見つかりません`);
          this.store.dispatch(navigate({ url: RoutingPathResolver.resolveBookmarkProblemLists() }));
          return;
        }

        // 正しいbookmarkProblemListIdが取得できた場合のみ、問題データの取得を行う
        this.setUpBookmarkProblemListProblems();
        this.setUpBookmarkSearching();
        this.findBookmarkProblemImages(1); // 問題および問題画像のデータの取得

        this.bookmarkProblemListName = bookmarkList.name;
        this.subjectId = bookmarkList.subjectId;
        this.subjectName = SUBJECT_NAMES[this.subjectId as SubjectId];
        this.totalBookmarkCount = bookmarkList.problemCount;
        this.lastUpdatedAt = Dates.stdDateStringFromIso(bookmarkList.updatedAt);
        this.visibility = bookmarkList.visibility;

        if (!this.isPrivate) {
          this.ownerName = bookmarkList.ownerName;
        }

        this.setUpReadableProblems(); // 問題データをReadableProblemに変換
        // this.findFirstPageProblems(); ページングがある場合
      });
  }

  ngOnDestroy() {
    this.store.dispatch(BookmarkProblemListActions.initializeCopyBookmarkProblemListState());
    this.store.dispatch(BookmarkProblemListActions.initializeFindBookmarkProblemListProblemImagesState());
    if (this.printPoller) {
      clearInterval(this.printPoller);
      this.printPoller = null;
    }
    if (this.printEndCallback) {
      this.printListenerService.clearOnPrintEnd(this.printEndCallback);
      this.printEndCallback = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    setTimeout(() => window.scrollTo(0, 0));
  }

  goBack() {
    if (!this.isPrivate) this.bookmarkProblemListRouterService.setState('sharedList');
    this.location.back();
  }

  updateBookmarkProblemList() {
    const data: ChangeBookmarkProblemListDialogData = {
      id: this.bookmarkProblemListId,
      listName: this.bookmarkProblemListName,
      visibility: this.visibility
    };
    const config: MatDialogConfig = {
      width: BOOKMARK_PROBLEM_LIST_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      autoFocus: false,
      data,
      disableClose: true
    };
    this.dialog
      .open(ChangeBookmarkProblemListDialogComponent, config)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.bookmarkProblemListName = result.response.name;
          this.visibility = result.response.visibility;
        }
      });
  }

  copyBookmarkProblemList() {
    Log.debug(this.LOG_SOURCE, 'お気に入りリストを複製します');

    this.store.dispatch(
      dispatchInfoMessage({
        message: `${this.bookmarkProblemListName}を複製しています。`
      })
    );

    const request = { bookmarkProblemListId: this.bookmarkProblemListId };
    this.store.dispatch(copyBookmarkProblemList(request));

    this.store
      .select(getCopyBookmarkProblemListResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        if (result.success) {
          const message = `${result.response.name} を登録しました`;
          this.store.dispatch(
            dispatchInfoMessage({
              message
            })
          );
          this.store.dispatch(initializeCopyBookmarkProblemListState());

          // Firestoreよりお気に入りリストに紐づく情報を再取得（リストはreducerでコピーされる）
          this.getBookmarkProblems();
          return;
        }

        Log.warn(this.LOG_SOURCE, `お気に入りリストの複製エラー: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
        const message = result.error ? `[${result.error.code}] ${result.error.message}` : 'エラーが発生しました';
        this.store.dispatch(
          dispatchInfoMessage({
            message
          })
        );
        this.store.dispatch(initializeCopyBookmarkProblemListState());
      });
  }

  deleteBookmarkProblemList() {
    const data: DeleteBookmarkProblemListDialogData = { id: this.bookmarkProblemListId, listName: this.bookmarkProblemListName };
    const config: MatDialogConfig = {
      width: BOOKMARK_PROBLEM_LIST_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      autoFocus: false,
      data,
      disableClose: true
    };
    this.dialog
      .open(DeleteBookmarkProblemListDialogComponent, config)
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.getBookmarkProblems(); // Firestoreより問題情報を再取得 - 削除されたリストの問題は不要のため
          this.store.dispatch(navigate({ url: RoutingPathResolver.resolveBookmarkProblemLists() }));
        }
      });
  }

  selectPage(page: number) {
    Log.debug(this.LOG_SOURCE, `選択された page: ${page} 並び順: ${this.selectedSortType.type}`);
    //this.scrollToTop();
    this.findBookmarkProblemImages(page);
    this.currentPage = page;
  }

  onChangeSubject() {
    this.currentPage = 1;
    this.findFirstPageProblems();
  }

  onChangeSortType() {
    this.currentPage = 1;
    this.findFirstPageProblems();
  }

  onDragDropped(event) {
    const request: ReorderBookmarkProblemListProblemsRequest = {
      bookmarkProblemListId: event.bookmarkProblemListId,
      problemIds: event.problemIds
    };
    this.store.dispatch(reorderBookmarkProblemListProblems(request));

    this.store
      .select(getReorderBookmarkProblemListProblemsResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        if (result.success) {
          this.store.dispatch(
            dispatchInfoMessage({
              message: 'お気に入りリストの問題の並び替えが完了しました。'
            })
          );
          this.store.dispatch(initializeReorderBookmarkProblemListProblemsState());
          return;
        }

        this.store.dispatch(
          dispatchInfoMessage({
            message: 'お気に入りリストの問題の並び替えに失敗しました。'
          })
        );
        this.store.dispatch(initializeReorderBookmarkProblemListProblemsState());
      });
  }

  showProblemDetail(problemObj: { problemId: string }) {
    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([user]) => {
        const condition: SearchBookmarkCondition = {
          userId: user.id,
          subjectId: this.subjectId,
          sortType: this.selectedSortType.type
        };

        const url = RoutingPathResolver.resolveProblemDetail(problemObj.problemId);
        const queryParams = QueryParamsMapper.encodeBookmarkSearchProblemsCondition(condition);
        Log.debug(this.LOG_SOURCE, 'お気に入り大問詳細画面用の query params: ', queryParams);
        this.store.dispatch(
          openWindow({
            url,
            extras: { queryParams: { ...queryParams, bookmarkProblemListId: this.bookmarkProblemListId, from: 'bookmark-problem-list' } }
          })
        );
      });
  }

  updateTotalProblemCount(problemObj: { problemCount: number }) {
    Log.debug(this.LOG_SOURCE, `お気に入りリストの問題数が更新されました: ${problemObj.problemCount}`);
    this.totalBookmarkCount = problemObj.problemCount;
    this.changeDetectorRef.detectChanges();
  }

  /* 印刷ページへ遷移するする処理はコメントアウト（お気に入りリスト問題一覧で直接印刷ダイアログを表示させるため）
  goToPrintPage(menuType: string) {
    this.store.dispatch(
      openWindow({
        url: RoutingPathResolver.resolveBookmarkProblemListsPrint(this.bookmarkProblemListId),
        extras: { queryParams: { subjectId: this.subjectId, menuType } }
      })
    );
  }
  */

  onPrintPreviewReady() {
    Log.debug(this.LOG_SOURCE, `印刷準備が完了しました。`);
    setTimeout(() => {
      this.isPrintReady = true;
    });
  }

  showPrintDialog(menuType: 'article' | 'spellcheck') {
    this.printMenuType = menuType;
    this.setUpBrowserTitle(menuType);

    this.printEndCallback = () => {
      Log.debug(this.LOG_SOURCE, `印刷完了`);
      this.printMenuType = null;
      this.isPrintReady = false;
      this.store.dispatch(setBrowserTitle({ subTitle: this.title })); // タイトルを戻すなど
    };
    this.printListenerService.onPrintEnd(this.printEndCallback);

    // 印刷準備ができるのを待つ (100msごとにポーリング)
    Log.debug(this.LOG_SOURCE, `印刷準備ができるのを待っています・・・`);
    // 連打で多重起動しないよう、既存ポーラーを止めてから開始
    if (this.printPoller) {
      clearInterval(this.printPoller);
    }
    this.printPoller = setInterval(() => {
      if (this.isPrintReady) {
        clearInterval(this.printPoller!);
        this.printPoller = null;
        this.store.dispatch(openPrintDialog());
        this.isPrintReady = false;
      }
    }, 100); // Poll every 100ms
  }

  private setUpBrowserTitle(menuType = 'article') {
    const type = menuType === 'article' ? '問題一覧' : '研究・解答一覧';
    const title = `${this.bookmarkProblemListName}_${type}`;
    this.store.dispatch(setBrowserTitle({ subTitle: `${title}` }));
  }

  // set ups ----------------------------------------------------------------------------------------------
  private setUpStaticData() {
    this.staticCommonData$ = this.store.select(getStaticCommonData).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpUser() {
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter<User>(it => it != null));
  }

  private setUpBookmarkProblemListId() {
    this.bookmarkProblemListId = this.activatedRoute.snapshot.paramMap.get('listId');
  }

  private setUpBookmarkProblemListProblems() {
    this.bookmarkProblemListProblems$ = this.store
      .select(BookmarkProblemListSelectors.getBookmarkProblemListProblems)
      .pipe(filter(it => it != null));
  }

  private setUpReadableProblems() {
    if (this.subjectId === SubjectId.ENGLISH) this.setUpReadableEnglishBookmarkProblems();
    if (this.subjectId === SubjectId.NATIONAL_LANGUAGE) this.setUpReadableNationalLanguageBookmarkProblems();
    if (this.subjectId === SubjectId.JAPANESE_HISTORY) this.setUpReadableJapaneseHistoryBookmarkProblems();
    if (this.subjectId === SubjectId.WORLD_HISTORY) this.setUpReadableWorldHistoryBookmarkProblems();

    if (SCIENCE_IDS.includes(this.subjectId)) {
      this.setUpReadableScienceBookmarkProblems(this.subjectId);
    }
  }

  private setUpReadableEnglishBookmarkProblems() {
    this.readableEnglishProblems$ = this.store.select(getReadableEnglishBookmarkProblemListProblems).pipe(
      filter(it => it !== null),
      shareReplay({ bufferSize: 1, refCount: true }) // 最後の1件の値をキャッシュして再購読時に即座に提供 + 最後の購読者が解除されたらソース Observable も自動的に解除される
    );

    // 問題データの初期化完了を通知するフラグの設定
    this.firstPageInitialized$ = this.readableEnglishProblems$.pipe(
      filter(it => it !== null),
      take(1),
      map(() => true),
      startWith(false)
    );
  }

  private setUpReadableScienceBookmarkProblems(subjectId: string) {
    const problems$ = this.store.select(getReadableScienceBookmarkProblemListProblemsBySubject(subjectId)).pipe(
      filter(it => it !== null),
      shareReplay({ bufferSize: 1, refCount: true }) // 最後の1件の値をキャッシュして再購読時に即座に提供 + 最後の購読者が解除されたらソース Observable も自動的に解除される
    );
    this.readableScienceProblemsForPrint$ = problems$; // 印刷用のデータを設定

    // 各科目の問題データを設定
    if (subjectId === SubjectId.MATH) this.readableMathProblems$ = problems$;
    if (subjectId === SubjectId.PHYSICS) this.readablePhysicsProblems$ = problems$;
    if (subjectId === SubjectId.CHEMISTRY) this.readableChemistryProblems$ = problems$;
    if (subjectId === SubjectId.BIOLOGY) this.readableBiologyProblems$ = problems$;
    if (subjectId === SubjectId.GEOGRAPHY) this.readableGeographyProblems$ = problems$;
    if (subjectId === SubjectId.POLITICAL_ECONOMY) this.readablePoliticalEconomyProblems$ = problems$;
    if (subjectId === SubjectId.SPECIAL_ADMISSIONS) this.readableSpecialAdmissionsProblems$ = problems$;

    // 問題データの初期化完了を通知するフラグの設定
    this.firstPageInitialized$ = problems$.pipe(
      filter(it => it !== null),
      take(1),
      map(() => true),
      startWith(false)
    );
  }

  private setUpReadableNationalLanguageBookmarkProblems() {
    this.readableNationalLanguageProblems$ = this.store.select(getReadableNationalLanguageBookmarkProblemListProblems).pipe(
      filter(it => it !== null),
      shareReplay({ bufferSize: 1, refCount: true }) // 最後の1件の値をキャッシュして再購読時に即座に提供 + 最後の購読者が解除されたらソース Observable も自動的に解除される
    );

    // 問題データの初期化完了を通知するフラグの設定
    this.firstPageInitialized$ = this.store.select(getReadableNationalLanguageBookmarkProblemListProblems).pipe(
      filter(it => it !== null),
      take(1),
      map(() => true),
      startWith(false)
    );
  }

  private setUpReadableJapaneseHistoryBookmarkProblems() {
    this.readableJapaneseHistoryProblems$ = this.store
      .select(getReadableHistoryBookmarkProblemListProblemsBySubject(SubjectId.JAPANESE_HISTORY))
      .pipe(
        filter(it => it !== null),
        shareReplay({ bufferSize: 1, refCount: true }) // 最後の1件の値をキャッシュして再購読時に即座に提供 + 最後の購読者が解除されたらソース Observable も自動的に解除される
      );

    // 問題データの初期化完了を通知するフラグの設定
    this.firstPageInitialized$ = this.store.select(getReadableHistoryBookmarkProblemListProblemsBySubject(SubjectId.JAPANESE_HISTORY)).pipe(
      filter(it => it !== null),
      take(1),
      map(() => true),
      startWith(false)
    );
  }

  private setUpReadableWorldHistoryBookmarkProblems() {
    this.readableWorldHistoryProblems$ = this.store
      .select(getReadableHistoryBookmarkProblemListProblemsBySubject(SubjectId.WORLD_HISTORY))
      .pipe(
        filter(it => it !== null),
        shareReplay({ bufferSize: 1, refCount: true }) // 最後の1件の値をキャッシュして再購読時に即座に提供 + 最後の購読者が解除されたらソース Observable も自動的に解除される
      );

    // 問題データの初期化完了を通知するフラグの設定
    this.firstPageInitialized$ = this.store.select(getReadableHistoryBookmarkProblemListProblemsBySubject(SubjectId.WORLD_HISTORY)).pipe(
      filter(it => it !== null),
      take(1),
      map(() => true),
      startWith(false)
    );
  }

  private setUpTotalPage(totalBookmarkCount: number) {
    this.totalPage = Math.ceil(totalBookmarkCount / BOOKMARK_RESULTS_PER_PAGE);
  }

  private setUpSelectableSortTypes() {
    const sortTypeDefault: SelectableSortType = {
      type: BookmarkSortType.CREATED_AT_DESC,
      displayName: BookmarkSortTypeDisplayName.CREATED_AT_DESC
    };
    this.selectableSortTypes = [
      sortTypeDefault,
      { type: BookmarkSortType.CREATED_AT_ASC, displayName: BookmarkSortTypeDisplayName.CREATED_AT_ASC }
    ];
    this.selectedSortType = sortTypeDefault;
  }

  private findFirstPageProblems() {
    const selector = this.isPrivate
      ? BookmarkProblemListSelectors.getBookmarkProblemLists
      : BookmarkProblemListSelectors.getSharedBookmarkProblemLists;

    this.store
      .select(selector)
      .pipe(
        filter(bookmarkProblemLists => bookmarkProblemLists != null),
        take(1)
      )
      .subscribe(bookmarkProblemLists => {
        const bookmarkList = bookmarkProblemLists.find(list => list.id === this.bookmarkProblemListId);
        if (!bookmarkList) {
          this.totalBookmarkCount = 0;
          Log.warn(this.LOG_SOURCE, `お気に入りリストが見つかりません`);
          this.store.dispatch(navigate({ url: RoutingPathResolver.resolveBookmarkProblemLists() }));
          return;
        }

        this.totalBookmarkCount = bookmarkList.problemCount;

        if (this.totalBookmarkCount === 0) {
          Log.warn(this.LOG_SOURCE, `条件に該当する大問が 0 件です`);
          // this.firstPageInitializedSubject.next(true);
          this.firstPageInitialized$ = of(true);
          return;
        }

        this.setUpTotalPage(this.totalBookmarkCount);
        this.selectPage(this.currentPage);
      });
  }

  private setUpBookmarkSearching() {
    this.bookmarkSearching$ = this.store.select(getBookmarkProblemListProblemsSearching);
  }

  private findBookmarkProblemImages(page: number) {
    this.store.dispatch(initializeFindBookmarkProblemListProblemImagesState());
    Log.debug(this.LOG_SOURCE, `${page} ページ目の ${this.bookmarkProblemListId} の問題と問題画像を取得します`);
    this.store.dispatch(findBookmarkProblemListProblemImages({ bookmarkProblemListId: this.bookmarkProblemListId }));
  }

  private getBookmarkProblems() {
    this.store.dispatch(BookmarkProblemsActions.initializeFindBookmarkProblemsState());

    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        const findBookmarkProblemsParams: FindBookmarkProblemsParams = {
          userId: signedInUser.id,
          subjectIds: signedInUser.visibleSubjectIds
        };
        this.store.dispatch(BookmarkProblemsActions.findBookmarkProblems(findBookmarkProblemsParams));
      });
  }
}
